import { supabase } from '../config/supabase'

/**
 * Obtiene el wedding_id del usuario (considerando si es partner)
 * @param {string} userId - ID del usuario (profile.id)
 * @returns {Promise<string|null>}
 */
const getUserWeddingId = async (userId) => {
  try {
    // Primero, obtener el perfil del usuario para verificar si es partner
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('partner_user_id')
      .eq('id', userId)
      .maybeSingle()

    if (profileError && profileError.code !== 'PGRST116') {
      console.error('Error fetching profile:', profileError)
    }

    // Determinar el user_id de la boda:
    // - Si el usuario tiene un partner_user_id (es partner), buscar la boda del usuario principal (partner_user_id)
    // - Si el usuario NO tiene partner_user_id pero hay otro perfil que tiene este usuario como partner_user_id,
    //   buscar la boda del usuario mismo (es el usuario principal)
    // - Si no tiene partner, buscar la boda del usuario mismo
    let weddingOwnerId = userId
    
    if (profile?.partner_user_id) {
      // Este usuario es un partner, buscar la boda del usuario principal
      weddingOwnerId = profile.partner_user_id
    } else {
      // Verificar si hay otro perfil que tenga este usuario como partner
      // Si es así, este usuario es el principal y debe buscar su propia boda
      const { data: partnerProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('partner_user_id', userId)
        .maybeSingle()
      
      // Si no hay partner que apunte a este usuario, buscar su propia boda
      weddingOwnerId = userId
    }

    const { data, error } = await supabase
      .from('weddings')
      .select('id')
      .eq('user_id', weddingOwnerId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle() // Usar maybeSingle() en lugar de single() para evitar 406

    if (error) {
      if (error.code === 'PGRST116') {
        return null
      }
      console.error('Error fetching wedding ID:', error)
      return null
    }

    return data?.id || null
  } catch (error) {
    console.error('Error fetching wedding ID:', error)
    return null
  }
}

/**
 * Obtiene todos los invitados del usuario
 * @param {string} userId - ID del usuario (profile.id)
 * @returns {Promise<Array>}
 */
export const getGuests = async (userId) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      return []
    }

    // Cargar todos los invitados
    const { data, error } = await supabase
      .from('guests')
      .select('*')
      .eq('wedding_id', weddingId)
      .order('first_name', { ascending: true })

    if (error) throw error

    return data || []
  } catch (error) {
    console.error('Error fetching guests:', error)
    return []
  }
}

/**
 * Crea un nuevo invitado
 * @param {string} userId - ID del usuario
 * @param {Object} guestData - Datos del invitado
 * @returns {Promise<Object|null>}
 */
export const createGuest = async (userId, guestData) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      throw new Error('No wedding found for user. Please create a wedding first.')
    }

    // Preparar los datos del invitado
    const firstName = (guestData.first_name || '').trim()
    const lastName = (guestData.last_name || '').trim()
    
    // Validar que first_name y last_name no estén vacíos
    if (!firstName || !lastName) {
      throw new Error('First name and last name are required.')
    }

    // Crear full_name concatenando first_name y last_name
    const fullName = `${firstName} ${lastName}`.trim()

    const insertData = {
      wedding_id: weddingId,
      first_name: firstName,
      last_name: lastName,
      full_name: fullName,
      email: guestData.email?.trim() || null,
      phone: guestData.phone?.trim() || null,
      rsvp_status: guestData.rsvp_status || 'pending',
      has_plus_one: guestData.has_plus_one || false,
      dietary_requirements: guestData.dietary_requirements?.trim() || null,
      allergies: guestData.allergies?.trim() || null,
      guest_list: guestData.guest_list?.trim() || null,
      guest_group: guestData.guest_group?.trim() || null,
      position: guestData.position?.trim() || null,
      notes: guestData.notes?.trim() || null,
    }

    // Convertir table_number a número si existe, sino null
    if (guestData.table_number) {
      const tableNum = parseInt(guestData.table_number, 10)
      insertData.table_number = !isNaN(tableNum) ? tableNum : null
    } else {
      insertData.table_number = null
    }

    const { data, error } = await supabase
      .from('guests')
      .insert([insertData])
      .select()
      .single()

    if (error) {
      console.error('Supabase error creating guest:', error)
      throw error
    }

    // Si hay plus one, crear también ese invitado y establecer la relación
    if (guestData.has_plus_one && guestData.plus_one_first_name?.trim() && guestData.plus_one_last_name?.trim()) {
      const plusOneFirstName = guestData.plus_one_first_name.trim()
      const plusOneLastName = guestData.plus_one_last_name.trim()
      const plusOneFullName = `${plusOneFirstName} ${plusOneLastName}`.trim()

      const plusOneData = {
        wedding_id: weddingId,
        first_name: plusOneFirstName,
        last_name: plusOneLastName,
        full_name: plusOneFullName,
        email: guestData.plus_one_email?.trim() || null,
        phone: guestData.plus_one_phone?.trim() || null,
        rsvp_status: 'pending', // El +1 tiene su propio estado RSVP, empieza en pending
        guest_of_id: data.id, // Establecer relación con el invitado principal
        has_plus_one: false, // El +1 no tiene su propio +1
        // IMPORTANTE: El +1 tiene sus propios datos, NO copiar del principal
        dietary_requirements: null, // Se edita individualmente
        allergies: null, // Se edita individualmente
        guest_list: guestData.guest_list?.trim() || null, // Mismo grupo/lista
        guest_group: guestData.guest_group?.trim() || null, // Mismo grupo
        table_number: null, // Se asigna individualmente
        position: null, // Se asigna individualmente
        notes: null, // Notas propias del +1
      }

      console.log('🔍 Creando +1 con guest_of_id:', data.id)
      const { error: plusOneError } = await supabase
        .from('guests')
        .insert([plusOneData])

      if (plusOneError) {
        console.error('Error creating plus one guest:', plusOneError)
        // No lanzar error aquí, solo loguear, para que el invitado principal se cree igual
      }
    }

    return data
  } catch (error) {
    console.error('Error creating guest:', error)
    throw error
  }
}

/**
 * Busca un guest por nombre en la misma boda (útil para encontrar +1 existente)
 * @param {string} weddingId - ID de la boda
 * @param {string} firstName - Nombre del guest
 * @param {string} lastName - Apellido del guest
 * @returns {Promise<Object|null>}
 */
const findGuestByName = async (weddingId, firstName, lastName) => {
  try {
    const { data, error } = await supabase
      .from('guests')
      .select('*')
      .eq('wedding_id', weddingId)
      .eq('first_name', firstName.trim())
      .eq('last_name', lastName.trim())
      .maybeSingle()

    if (error && error.code !== 'PGRST116') {
      console.error('Error finding guest by name:', error)
    }

    return data || null
  } catch (error) {
    console.error('Error finding guest by name:', error)
    return null
  }
}

/**
 * Actualiza un invitado
 * @param {string} guestId - ID del invitado
 * @param {Object} updates - Campos a actualizar
 * @returns {Promise<Object|null>}
 */
export const updateGuest = async (guestId, updates) => {
  try {
    const { data, error } = await supabase
      .from('guests')
      .update(updates)
      .eq('id', guestId)
      .select()
      .single()

    if (error) throw error

    return data
  } catch (error) {
    console.error('Error updating guest:', error)
    throw error
  }
}

/**
 * Elimina un invitado
 * @param {string} guestId - ID del invitado
 * @returns {Promise<boolean>}
 */
export const deleteGuest = async (guestId) => {
  try {
    const { error } = await supabase
      .from('guests')
      .delete()
      .eq('id', guestId)

    if (error) throw error

    return true
  } catch (error) {
    console.error('Error deleting guest:', error)
    throw error
  }
}

/**
 * Obtiene estadísticas de invitados por estado RSVP
 * @param {string} userId - ID del usuario
 * @returns {Promise<Object>}
 */
export const getGuestStats = async (userId) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      return {
        all: 0,
        confirmed: 0,
        declined: 0,
        pending: 0,
        not_invited: 0,
      }
    }

    const { data, error } = await supabase
      .from('guests')
      .select('rsvp_status')
      .eq('wedding_id', weddingId)

    if (error) throw error

    const stats = {
      all: data?.length || 0,
      confirmed: data?.filter((g) => g.rsvp_status === 'confirmed').length || 0,
      declined: data?.filter((g) => g.rsvp_status === 'declined').length || 0,
      pending: data?.filter((g) => g.rsvp_status === 'pending').length || 0,
      not_invited: data?.filter((g) => g.rsvp_status === 'not_invited' || !g.rsvp_status).length || 0,
    }

    return stats
  } catch (error) {
    console.error('Error fetching guest stats:', error)
    return {
      all: 0,
      confirmed: 0,
      declined: 0,
      pending: 0,
      not_invited: 0,
    }
  }
}
